import { useTranslation } from 'react-i18next';
import { useSettingsStore, type TextScale } from '@/lib/settings-store';
import styles from './TextSizeToggle.module.css';

/**
 * Settings-page row for the body-text size preference. Three-step
 * segmented control: Default / Large / Extra Large. Each button's label
 * renders at its own target scale so users see the size difference
 * immediately — "Extra Large" literally appears larger than "Default"
 * in the button itself, before they tap.
 *
 * Scale values live in index.css — flipping this preference sets
 * html[data-text-scale="..."] which changes the root font-size, and
 * every rem-based typography site in the app scales with it. Chrome,
 * buttons, pills, safe-area math, and touch targets are px-based and
 * intentionally do not scale.
 */
export default function TextSizeToggle() {
  const { t } = useTranslation();
  const textScale = useSettingsStore((s) => s.textScale);
  const setTextScale = useSettingsStore((s) => s.setTextScale);

  const options: Array<{ value: TextScale; labelKey: string; size: string }> = [
    { value: 'default', labelKey: 'settings.textSizeDefault', size: '15px' },
    { value: 'large', labelKey: 'settings.textSizeLarge', size: '17px' },
    { value: 'xl', labelKey: 'settings.textSizeXL', size: '19px' },
  ];

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.segments}
        role="radiogroup"
        aria-label={t('settings.textSizeLabel')}
      >
        {options.map((opt) => {
          const active = textScale === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={`${styles.segment} ${active ? styles.segmentActive : ''}`}
              onClick={() => setTextScale(opt.value)}
              // Inline size so each button shows its label at its own scale.
              // Independent of the currently-applied root scale — users see
              // "Extra Large" visibly larger than "Default" regardless of
              // which one is active.
              style={{ fontSize: opt.size }}
            >
              {t(opt.labelKey)}
            </button>
          );
        })}
      </div>
      <p className={styles.description}>{t('settings.textSizeHelper')}</p>
    </div>
  );
}
